import { useAppSelector } from '@/store/hooks';
import type { ImageProps } from '@builder4/shared/components/Image';
import type { TRBLNumberCOORDS } from '@builder4/shared/types/layout';
import { useEditor, useNode } from '@craftjs/core';
import clsx from 'clsx';
import React, { useRef, useState, useEffect } from 'react';
import { selectScale } from '@/store/zoom/selectors';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { IMAGE_NODE_NAME } from '@/utils/constants';

export const ImageRadiusControls = () => {
  const { actions } = useEditor();
  const {
    actions: { setProp },
    id,
    nodeProps,
  } = useNode((node) => {
    return {
      nodeProps: node.data.props as Partial<ImageProps>,
      dom: node.dom,
    };
  });

  const { getBPProp, setBPProp } = useBreakpointAwareProp(IMAGE_NODE_NAME);

  // Read props using breakpoint-aware getter
  const radius = getBPProp(nodeProps, 'borderRadius');
  const nodeMargin = getBPProp(nodeProps, 'margin');

  const [localBorderRadius, setLocalBorderRadius] = useState<TRBLNumberCOORDS>(
    radius ?? [0, 0, 0, 0],
  );
  const [initialRadius, setInitialRadius] = useState<TRBLNumberCOORDS>(radius ?? [0, 0, 0, 0]);
  const [topLeft, topRight, bottomRight, bottomLeft] = localBorderRadius;

  useEffect(() => {
    if (!Array.isArray(radius)) return;
    const nextRadius = radius;
    setLocalBorderRadius((prev) => {
      const isSame =
        nextRadius[0] === prev[0] &&
        nextRadius[1] === prev[1] &&
        nextRadius[2] === prev[2] &&
        nextRadius[3] === prev[3];
      return isSame ? prev : nextRadius;
    });
  }, [radius]);

  // Read current margin values to offset handles accordingly
  const [marginTop = 0, marginRight = 0, marginBottom = 0, marginLeft = 0] = nodeMargin || [
    0, 0, 0, 0,
  ];

  const updateBorderRadius = (
    topLeft: number,
    topRight: number,
    bottomRight: number,
    bottomLeft: number,
  ) => {
    setLocalBorderRadius([topLeft, topRight, bottomRight, bottomLeft]);
    // if (dom) {
    //   (dom.children[0] as HTMLElement).style.borderRadius =
    //     `${topLeft}px ${topRight}px ${bottomRight}px ${bottomLeft}px`;
    // }
    actions.history.ignore().setProp(id, (props: Partial<ImageProps>) => {
      setBPProp(props, 'borderRadius', [topLeft, topRight, bottomRight, bottomLeft]);
    });
  };

  const updateBorderRadiusComplete = (
    topLeft: number,
    topRight: number,
    bottomRight: number,
    bottomLeft: number,
  ) => {
    actions.history.ignore().setProp(id, (props: ImageProps) => {
      setBPProp(props, 'borderRadius', [...initialRadius]);
    });
    setInitialRadius([topLeft, topRight, bottomRight, bottomLeft]);
    setProp((props: Partial<ImageProps>) => {
      setBPProp(props, 'borderRadius', [topLeft, topRight, bottomRight, bottomLeft]);
    });
  };

  // Function to update all corners with the same value
  const updateAllCorners = (newValue: number) => {
    updateBorderRadius(newValue, newValue, newValue, newValue);
  };

  const updateAllCornersComplete = (newValue: number) => {
    updateBorderRadiusComplete(newValue, newValue, newValue, newValue);
  };

  return (
    <>
      <RadiusControl
        value={topLeft}
        type="topLeft"
        offsetX={marginLeft}
        offsetY={marginTop}
        onUpdate={(newValue, isAltPressed) => {
          if (isAltPressed) {
            // Update only this corner
            updateBorderRadius(newValue, topRight, bottomRight, bottomLeft);
          } else {
            // Update all corners
            updateAllCorners(newValue);
          }
        }}
        onComplete={(newValue, isAltPressed) => {
          if (isAltPressed) {
            // Update only this corner
            updateBorderRadiusComplete(newValue, topRight, bottomRight, bottomLeft);
          } else {
            // Update all corners
            updateAllCornersComplete(newValue);
          }
        }}
      />
      <RadiusControl
        value={topRight}
        type="topRight"
        offsetX={marginRight}
        offsetY={marginTop}
        onUpdate={(newValue, isAltPressed) => {
          if (isAltPressed) {
            // Update only this corner
            updateBorderRadius(topLeft, newValue, bottomRight, bottomLeft);
          } else {
            // Update all corners
            updateAllCorners(newValue);
          }
        }}
        onComplete={(newValue, isAltPressed) => {
          if (isAltPressed) {
            // Update only this corner
            updateBorderRadiusComplete(topLeft, newValue, bottomRight, bottomLeft);
          } else {
            // Update all corners
            updateAllCornersComplete(newValue);
          }
        }}
      />
      <RadiusControl
        value={bottomLeft}
        type="bottomLeft"
        offsetX={marginLeft}
        offsetY={marginBottom}
        onUpdate={(newValue, isAltPressed) => {
          if (isAltPressed) {
            // Update only this corner
            updateBorderRadius(topLeft, topRight, bottomRight, newValue);
          } else {
            // Update all corners
            updateAllCorners(newValue);
          }
        }}
        onComplete={(newValue, isAltPressed) => {
          if (isAltPressed) {
            // Update only this corner
            updateBorderRadiusComplete(topLeft, topRight, bottomRight, newValue);
          } else {
            // Update all corners
            updateAllCornersComplete(newValue);
          }
        }}
      />
      <RadiusControl
        value={bottomRight}
        type="bottomRight"
        offsetX={marginRight}
        offsetY={marginBottom}
        onUpdate={(newValue, isAltPressed) => {
          if (isAltPressed) {
            // Update only this corner
            updateBorderRadius(topLeft, topRight, newValue, bottomLeft);
          } else {
            // Update all corners
            updateAllCorners(newValue);
          }
        }}
        onComplete={(newValue, isAltPressed) => {
          if (isAltPressed) {
            // Update only this corner
            updateBorderRadiusComplete(topLeft, topRight, newValue, bottomLeft);
          } else {
            // Update all corners
            updateAllCornersComplete(newValue);
          }
        }}
      />
    </>
  );
};

const RadiusControl = ({
  value,
  type,
  offsetX = 0,
  offsetY = 0,
  onUpdate,
  onComplete,
}: {
  value: number;
  type: 'topLeft' | 'topRight' | 'bottomRight' | 'bottomLeft';
  offsetX?: number;
  offsetY?: number;
  onUpdate: (newValue: number, isAltPressed: boolean) => void;
  onComplete: (newValue: number, isAltPressed: boolean) => void;
}) => {
  const scale = useAppSelector(selectScale);

  const [localValue, setLocalValue] = useState(value);
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [startValue, setStartValue] = useState(0);
  const [maxRadius, setMaxRadius] = useState(0);
  const [cursor, setCursor] = useState('');
  const [position, setPosition] = useState('');
  const [transformX, setTransformX] = useState('');
  const [transformY, setTransformY] = useState('');

  const controlRef = useRef<HTMLDivElement>(null);
  const finalValueOnComplete = useRef(0);
  const { node } = useNode((node) => ({ node }));

  const cursorPointer = {
    topLeft: 'nw-resize',
    topRight: 'ne-resize',
    bottomRight: 'se-resize',
    bottomLeft: 'sw-resize',
  };
  const positionClass = {
    topLeft: 'top-2 left-2',
    topRight: 'top-2 right-2',
    bottomRight: 'bottom-2 right-2',
    bottomLeft: 'bottom-2 left-2',
  };
  useEffect(() => {
    setCursor(cursorPointer[type]);
    setPosition(positionClass[type]);
    setTransformX(type === 'topLeft' || type === 'bottomLeft' ? '+' : '-');
    setTransformY(type === 'topLeft' || type === 'topRight' ? '+' : '-');
  }, [type]);

  useEffect(() => {
    if (localValue != value) setLocalValue(value);
  }, [value]);

  // Calculate max radius on mount
  useEffect(() => {
    if (!node?.dom) return;
    const rect = node.dom.getBoundingClientRect();
    const minDimension = Math.min(rect.width, rect.height) / scale;
    const nextMaxRadius = Math.floor(minDimension / 2);
    setMaxRadius((prev) => (prev === nextMaxRadius ? prev : nextMaxRadius));
  }, [node?.dom, scale]);

  useEffect(() => {
    if (maxRadius && localValue > maxRadius) setLocalValue(maxRadius);
  }, [maxRadius, localValue]);

  const handleMouseDown = (e: React.MouseEvent) => {
    document.body.style.cursor = cursor;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setStartPos({ x: e.clientX, y: e.clientY });
    setStartValue(value);
    finalValueOnComplete.current = value;
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;

    const deltaX = e.clientX - startPos.x;
    const deltaY = e.clientY - startPos.y;

    // Calculate direction based on corner type using immediate movement
    let newValue = 0;
    let delta = 0;
    if (type === 'topLeft') {
      // Use the cumulative delta for the actual value change
      delta = Math.max(deltaX, deltaY);
    } else if (type === 'topRight') {
      if (deltaX < 0 && deltaY > 0) {
        //decrease radius
        delta = Math.max(deltaX, deltaY);
      } else {
        //increase radius
        delta = -1 * Math.max(deltaX, deltaY);
      }
    } else if (type === 'bottomRight') {
      if (deltaX > 0 && deltaY > 0) {
        //decrease radius
        delta = -1 * Math.min(deltaX, deltaY);
      } else {
        //increase radius
        delta = -1 * Math.min(deltaX, deltaY);
      }
    } else if (type === 'bottomLeft') {
      if (deltaX < 0 && deltaY > 0) {
        //decrease radius
        delta = Math.min(deltaX, deltaY);
      } else {
        //increase radius
        delta = Math.max(deltaX, deltaY);
      }
    }

    newValue = Math.round(Math.max(0, Math.min(maxRadius, startValue + delta / scale)));
    setLocalValue(newValue);
    finalValueOnComplete.current = newValue;
    onUpdate(newValue, e.altKey);
  };

  const handleMouseUp = (e: MouseEvent) => {
    setIsDragging(false);
    document.body.style.cursor = 'default';
    onComplete(finalValueOnComplete.current, e.altKey);
  };

  // Add global mouse event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, startPos, startValue]);

  return (
    <div
      ref={controlRef}
      onMouseDown={handleMouseDown}
      style={{
        cursor,
        transform: `translate(${transformX}${localValue + offsetX}px,${transformY}${localValue + offsetY}px)`,
      }}
      className={clsx(
        't-center pointer-events-auto absolute z-9999 h-2 w-2 rounded bg-white outline outline-1 outline-green-500',
        position,
      )}
    ></div>
  );
};
